import { lazy, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "./hooks/auth/use-auth";
import { Toaster } from "./components/ui/toaster";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n/i18n";
import AdminPanelLayout from "./components/admin-panel/admin-panel-layout";
import Loader from "./components/loader";
import { ThemeProvider } from "@/components/theme-provider";
import { ContentLayout } from "./components/admin-panel/content-layout";
import { SuspenseLoader } from "./components/suspense-loader";
import { useDispatch } from "react-redux";
import { setTheme, setLanguage } from "./store/store";
import Cookies from "js-cookie";
import { Helmet } from "react-helmet";
import Contact from "./pages/contact";
import { useUser } from "./hooks/user/use-user";

const DEFAULT_DESCRIPTION =
  "StudExam - Plateforme d'examens en ligne pour étudiants et enseignants";
const DEFAULT_IMAGE = "/og-image.png";
const DEFAULT_URL = "https://studexam.com";
const DEFAULT_TITLE = "StudExam";

const Dashboard = lazy(() => import("./pages/dashboard"));
const Register = lazy(() => import("./pages/register"));
const Search = lazy(() => import("./pages/search"));
const DoingExam = lazy(() => import("./pages/doing-exam"));
const Login = lazy(() => import("./pages/login"));
const LandingPage = lazy(() => import("./pages/landing-page"));
const ExamOrExerciseEditor = lazy(
  () => import("./pages/exam-or-exercise-editor")
);
const VerifyEmail = lazy(() => import("./pages/verify-email"));
const ForgottenPassword = lazy(() => import("./pages/forgotten-password"));
const ImportContestExam = lazy(() => import("./pages/import-contest-exam"));
const UserExams = lazy(() => import("./pages/user-exams"));
const Logout = lazy(() => import("./pages/logout"));
const Settings = lazy(() => import("./pages/settings"));
const UpdateEmail = lazy(() => import("./pages/update-email"));
const MyGroups = lazy(() => import("./pages/my-groups"));
const MyExamSharings = lazy(() => import("./pages/my-exam-sharings"));
const ExamsSharedToMe = lazy(() => import("./pages/exams-shared-to-me"));
const SeeStudentAnswersForSharedExam = lazy(
  () => import("./pages/see-student-answers-for-shared-exam")
);
const UserEmailNotVerified = lazy(
  () => import("./pages/user-email-not-verified")
);
const AdminSearch = lazy(() => import("./pages/admin-search"));
const MentionsLegales = lazy(() => import("./pages/mentions-legales"));
const FAQ = lazy(() => import("./pages/faq"));
const CGU = lazy(() => import("./pages/cgu"));
const BasicInfoPage = lazy(
  () => import("./pages/exam-or-exercise-editor/basic-info")
);
const SegmentationPage = lazy(
  () => import("./pages/exam-or-exercise-editor/segmentation")
);
const SolutionsPage = lazy(
  () => import("./pages/exam-or-exercise-editor/solutions")
);
const SolutionsFileSegmentationPage = lazy(
  () => import("./pages/exam-or-exercise-editor/solutions-file-segmentation")
);
const CodeEditorPage = lazy(
  () => import("./pages/exam-or-exercise-editor/code-editor")
);
const AppUsers = lazy(() => import("./pages/app-users"));
const NewConversation = lazy(
  () => import("./pages/conversations/new-conversation")
);
const Conversation = lazy(() => import("./pages/conversations/conversation"));
const MyConversations = lazy(
  () => import("./pages/conversations/my-conversations")
);

const NewAiInterview = lazy(
  () => import("./pages/ai-interviews/new-ai-interview")
);
const MyAiInterviews = lazy(
  () => import("./pages/ai-interviews/my-ai-interviews")
);
const AiInterview = lazy(() => import("./pages/ai-interviews/ai-interview"));
const AdminAiInterviews = lazy(
  () => import("./pages/ai-interviews/admin-ai-interviews")
);
const AdminAiInterviewDirect = lazy(
  () => import("./pages/ai-interviews/admin-ai-interview-direct")
);
const Cart = lazy(() => import("./pages/user-orders/cart"));
const Checkout = lazy(() => import("./pages/user-orders/checkout"));
const OrderConfirmation = lazy(
  () => import("./pages/user-orders/order-confirmation")
);
const AdminAiInterviewTemplateManagement = lazy(
  () => import("./pages/ai-interviews/admin-ai-interview-template-management")
);

const publicRoutes = [
  {
    path: "/",
    element: <LandingPage />,
    title: "Accueil",
    description:
      "Bienvenue sur StudExam, la plateforme d'examens en ligne innovante pour les étudiants et les enseignants.",
  },
  {
    path: "/login",
    element: <Login />,
    title: "Connexion",
    description:
      "Connectez-vous à votre compte StudExam pour accéder à vos examens et exercices.",
  },
  { path: "/contact", element: <Contact />, title: "Contact" },
  { path: "/logout", element: <Logout />, title: "Déconnexion" },
  { path: "/register", element: <Register />, title: "Inscription" },
  {
    path: "/forgot-password",
    element: <ForgottenPassword />,
    title: "Mot de passe oublié",
  },
  {
    path: "/verify-email/:token",
    element: <VerifyEmail />,
    title: "Vérification email",
  },
  {
    path: "/user-email-not-verified",
    element: <UserEmailNotVerified />,
    title: "Email non vérifié",
  },
  {
    path: "/verify-updated-email/:token",
    element: <UpdateEmail />,
    title: "Mise à jour email",
  },
  {
    path: "/mentions-legales",
    element: <MentionsLegales />,
    title: "Mentions légales",
  },
  { path: "/faq", element: <FAQ />, title: "FAQ" },
  { path: "/cgu", element: <CGU />, title: "CGU" },
];

const protectedRoutes: {
  path: string;
  element: React.ReactNode;
  title: string;
  description?: string;
  role?: "operator" | "admin";
}[] = [
  {
    path: "dashboard",
    element: <Dashboard />,
    title: "Tableau de bord",
    description:
      "Gérez vos examens, suivez vos progrès et accédez à vos ressources sur StudExam.",
  },
  { path: "search", element: <Search />, title: "Recherche" },
  {
    path: "admin-search",
    element: <AdminSearch />,
    title: "Recherche Admin",
    role: "operator",
  },
  { path: "settings", element: <Settings />, title: "Paramètres" },
  { path: "user-exams", element: <UserExams />, title: "Mes examens" },
  {
    path: "my-exam-sharings",
    element: <MyExamSharings />,
    title: "Mes partages d'examens",
  },
  {
    path: "exams-shared-to-me",
    element: <ExamsSharedToMe />,
    title: "Examens partagés avec moi",
  },
  { path: "my-groups", element: <MyGroups />, title: "Mes groupes" },
  {
    path: "exam-or-exercise-editor/:slug",
    element: <ExamOrExerciseEditor />,
    title: "Éditeur d'examen",
  },
  {
    path: "editor/:slug/basic-info",
    element: <BasicInfoPage />,
    title: "Informations de base",
  },
  {
    path: "editor/:slug/segmentation",
    element: <SegmentationPage />,
    title: "Segmentation",
  },
  {
    path: "editor/:slug/solutions",
    element: <SolutionsPage />,
    title: "Solutions",
  },
  {
    path: "editor/:slug/solutions-file-segmentation",
    element: <SolutionsFileSegmentationPage />,
    title: "Segmentation des fichiers de solutions",
  },
  {
    path: "editor/:slug/code/:language",
    element: <CodeEditorPage />,
    title: "Éditeur de code",
  },
  {
    path: "see-student-answers-for-shared-exam/:slug",
    element: <SeeStudentAnswersForSharedExam />,
    title: "Réponses des étudiants",
  },
  {
    path: "doing-exam/:slug",
    element: <DoingExam />,
    title: "Examen en cours",
  },
  {
    path: "import-contest-exam",
    element: <ImportContestExam />,
    title: "Importer un examen",
    role: "operator",
  },
  {
    path: "app-users",
    element: <AppUsers />,
    title: "Utilisateurs",
    role: "admin",
  },
  {
    path: "new-conversation",
    element: <NewConversation />,
    title: "Nouvelle conversation",
  },
  {
    path: "conversation/:id",
    element: <Conversation />,
    title: "Conversation",
  },
  {
    path: "my-conversations",
    element: <MyConversations />,
    title: "Mes conversations",
  },
  {
    path: "new-ai-interview",
    element: <NewAiInterview />,
    title: "Nouvel entretien IA",
  },
  {
    path: "my-ai-interviews",
    element: <MyAiInterviews />,
    title: "Mes entretiens",
  },
  {
    path: "ai-interview/:id",
    element: <AiInterview />,
    title: "Entretien IA",
  },
  {
    path: "admin-ai-interviews",
    element: <AdminAiInterviews />,
    title: "Entretiens IA (Admin)",
    role: "admin",
  },
  {
    path: "admin-ai-interview-for-testing",
    element: <AdminAiInterviewDirect />,
    title: "Entretien IA de Test",
    role: "admin",
  },
  {
    path: "admin-ai-interview-template-management",
    element: <AdminAiInterviewTemplateManagement />,
    title: "Gestion des modèles d'entretiens IA",
    role: "admin",
  },
  {
    path: "cart",
    element: <Cart />,
    title: "Panier",
  },
  {
    path: "checkout/:reference",
    element: <Checkout />,
    title: "Checkout",
  },
  {
    path: "order-confirmation/:reference",
    element: <OrderConfirmation />,
    title: "Confirmation de commande",
  },
];

function RoleProtection({
  children,
  requiredRole,
}: {
  children: React.ReactNode;
  requiredRole?: "operator" | "admin";
}) {
  const { user, isLoading } = useUser();

  if (isLoading) return <Loader />;

  if (!requiredRole) return <>{children}</>;

  if (requiredRole === "admin" && !user?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole === "operator" && !user?.isAtLeastOperator) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AuthProtectedLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAuthStatusLoading, isEmailVerified } = useAuth();

  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();

  const redirectSearchParams = new URLSearchParams();

  if (pathname !== "/login") {
    const redirectUrl = new URL(pathname, window.location.origin);

    redirectUrl.search = searchParams.toString();

    redirectSearchParams.set("redirect", redirectUrl.toString());
  }

  const loginUrlWithRedirect = `/login?${redirectSearchParams.toString()}`;

  if (isAuthStatusLoading) {
    return (
      <div
        className={
          "w-full h-full min-h-screen flex justify-center items-center"
        }
      >
        <Loader />
      </div>
    );
  }

  if (!isAuthStatusLoading && !isAuthenticated) {
    return <Navigate to={loginUrlWithRedirect} replace />;
  }

  if (!isAuthStatusLoading && !isEmailVerified) {
    return <Navigate to="/user-email-not-verified" replace />;
  }

  return (
    <AdminPanelLayout>
      <ContentLayout>{children}</ContentLayout>
    </AdminPanelLayout>
  );
}

function RouteWithTitle({
  element,
  title,
  description,
}: {
  element: React.ReactNode;
  title: string;
  description?: string;
}) {
  const currentUrl = window.location.href;

  return (
    <>
      <Helmet>
        <title>{`${title} | ${DEFAULT_TITLE}`}</title>
        <meta name="description" content={description || DEFAULT_DESCRIPTION} />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${title} | ${DEFAULT_TITLE}`} />
        <meta
          property="og:description"
          content={description || DEFAULT_DESCRIPTION}
        />
        <meta property="og:image" content={DEFAULT_IMAGE} />
        <meta property="og:site_name" content={DEFAULT_TITLE} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:domain"
          content={new URL(DEFAULT_URL).hostname}
        />
        <meta property="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={`${title} | ${DEFAULT_TITLE}`} />
        <meta
          name="twitter:description"
          content={description || DEFAULT_DESCRIPTION}
        />
        <meta name="twitter:image" content={DEFAULT_IMAGE} />

        {/* Autres métadonnées importantes */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={currentUrl} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {element}
    </>
  );
}

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const savedTheme = Cookies.get("theme") as
      | "dark"
      | "light"
      | "system"
      | undefined;
    const savedLanguage = Cookies.get("language") as "fr" | "en" | undefined;

    if (savedTheme) {
      dispatch(setTheme(savedTheme));
    }

    if (savedLanguage) {
      dispatch(setLanguage(savedLanguage));
    }
  }, [dispatch]);

  return (
    <I18nextProvider i18n={i18n} defaultNS="translation">
      <ThemeProvider defaultTheme="light">
        <Helmet>
          <html lang={i18n.language} />
          <meta charSet="utf-8" />
          <link rel="icon" href="/favicon.ico" />
          <meta name="theme-color" content="#ffffff" />

          <title>StudExam</title>
          <meta name="description" content={DEFAULT_DESCRIPTION} />
          <meta property="og:site_name" content="StudExam" />
          <meta property="og:locale" content={i18n.language} />
        </Helmet>

        <BrowserRouter>
          <Routes>
            {publicRoutes.map(({ path, element, title, description }) => (
              <Route
                key={path}
                path={path}
                element={
                  <SuspenseLoader>
                    <RouteWithTitle
                      element={element}
                      title={title}
                      description={description}
                    />
                  </SuspenseLoader>
                }
              />
            ))}

            <Route
              path="/*"
              element={
                <AuthProtectedLayout>
                  <Routes>
                    {protectedRoutes.map(
                      ({ path, element, title, description, role }) => (
                        <Route
                          key={path}
                          path={path}
                          element={
                            <SuspenseLoader>
                              <RoleProtection requiredRole={role}>
                                <RouteWithTitle
                                  element={element}
                                  title={title}
                                  description={description}
                                />
                              </RoleProtection>
                            </SuspenseLoader>
                          }
                        />
                      )
                    )}
                  </Routes>
                </AuthProtectedLayout>
              }
            />
          </Routes>

          <Toaster />
        </BrowserRouter>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;
